import { Chip, CircularProgress, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import foundersAPI, { Founder, FounderId } from '../../api/founders';
import gatheringsAPI, { Gathering } from '../../api/gatherings';
import { GatheringContext } from '../../contexts/gathering-context';
import FounderAutocomplete from '../autocomplete/founder-autocomplete';
import {
  AssignmentRecommendedTable,
  Button,
  FormButtons,
  FormGroup,
  TextField,
} from '../common';

export interface AssignFounders {
  assigned: FounderId[];
  removed: FounderId[];
  all: InviteFounder[];
  isAll: boolean;
}

interface AssignFoundersFormProps {
  gatheringId: Gathering['id'];
  loading?: boolean;
  onSuccess: (params: AssignFounders) => any;
  onCancel: () => any;
}

export interface InviteFounder {
  name: string;
  id: FounderId;
  email: string;
}

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: 285,
    height: '100%',
  },
  formButtons: {
    alignItems: 'flex-end',
    flexGrow: 1,
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'row wrap',
    margin: '15px 0 0 -10px',
  },
  chipItem: {
    margin: '0 0 10px 10px',
    overflow: 'hidden',

    [theme.breakpoints.up('sm')]: {
      overflow: 'initial',
    },
  },
}));

const parseFounders = (value: Founder[]): InviteFounder[] => {
  if (value.length === 0) {
    return [];
  }

  return value.map((item) => ({
    name: `${item.firstName} ${item.lastName}`,
    id: item.id,
    email: item.email,
  }));
};

function InviteGatheringFounders({
  gatheringId,
  loading = false,
  onSuccess,
  onCancel,
}: AssignFoundersFormProps) {
  const classes = useStyles();
  const { gathering } = useContext(GatheringContext);
  const [loadedFounders, setLoadedFounders] = useState<InviteFounder[]>([]);
  const [initialFounders, setInitialFounders] = useState<InviteFounder[]>([]);
  const [recommended, setRecommended] = useState<InviteFounder[]>();
  const [displayedFounders, setDisplayedFounders] = useState<InviteFounder[]>(
    [],
  );
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [isLoading, setIsLoading] = useState<{
    founders: boolean;
    recommended: boolean;
  }>({ founders: false, recommended: !recommended });
  const [searchIteration, setSearchIteration] = useState(0);

  const handleSelectedAll = () => {
    setInitialFounders([]);
    setIsSelectedAll(true);
  };

  const handleFoundersSearch = useCallback((searchedFounders: Founder[]) => {
    setDisplayedFounders(parseFounders(searchedFounders));
  }, []);

  const handleFounderRemove = (founder: InviteFounder) => {
    setInitialFounders((prevFounders) =>
      prevFounders.filter((prevFounder) => prevFounder.id !== founder.id),
    );
  };

  const handleFounderAdd = (founder: InviteFounder) => {
    setInitialFounders((prevFounders) => [...prevFounders, founder]);
    setSearchIteration((prevIteration) => prevIteration + 1);
  };

  const loadInitialFounders = async (gatheringId: Gathering['id']) => {
    try {
      const loadedFounders = await gatheringsAPI.getGatheringFounderList(
        gatheringId,
      );
      const parsedFounders = parseFounders(loadedFounders);
      setLoadedFounders(parsedFounders);
      setInitialFounders([]);

      const result = await foundersAPI.getRecommendedFoundersByVentures();

      const loadedFoundersIds = loadedFounders.map(({ id }) => id);
      const parsedRecommendedFounders = parseFounders(result).filter(
        (value) => !loadedFoundersIds.includes(value.id),
      );

      setRecommended(parsedRecommendedFounders);
      setIsLoading({
        founders: false,
        recommended: false,
      });
    } catch (e: any) {
      setIsLoading({
        recommended: false,
        founders: false,
      });
    }
  };

  const handleSubmit = async ({
    foundersIds,
  }: {
    foundersIds: Founder['id'][];
  }) => {
    const loadedFoundersIds = loadedFounders.map((founder) => founder.id);
    const assigned = foundersIds.filter(
      (id) => !loadedFoundersIds.includes(id),
    );
    const all = initialFounders.filter((founder) =>
      foundersIds.includes(founder.id),
    );

    onSuccess({ assigned, removed: [], all, isAll: isSelectedAll });
  };

  useEffect(() => {
    if (gathering) {
      loadInitialFounders(gathering.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gathering]);

  if (isLoading.founders || isLoading.recommended) {
    return (
      <div className={classes.form}>
        <div className={classes.loader}>
          <CircularProgress size={36} color='primary' />
        </div>
        <FormButtons>
          <Button onClick={onCancel} variant='outlined'>
            Cancel
          </Button>
          <Button disabled>Apply</Button>
        </FormButtons>
      </div>
    );
  }

  const initialValues = {
    foundersIds: initialFounders.map((founder) => founder.id),
  };

  const filteredDisplayedFounders = displayedFounders.filter(
    (founder) =>
      !initialFounders.find(
        (initialFounder) => initialFounder.id === founder.id,
      ),
  );
  const filteredRecommendedFounders = (recommended || []).filter(
    (item) => !initialFounders.find((founder) => founder.id === item.id),
  );

  const displayedList =
    filteredDisplayedFounders.length > 0
      ? filteredDisplayedFounders
      : filteredRecommendedFounders;

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      keepDirtyOnReinitialize
      render={(formProps) => (
        <form
          className={classes.form}
          onSubmit={formProps.handleSubmit}
          noValidate>
          <FormGroup>
            <FounderAutocomplete
              iteration={searchIteration}
              onSearch={handleFoundersSearch}
              inputRenderer={({ value, onChange, isLoading }) => (
                <TextField
                  value={value}
                  label='Add a founder'
                  placeholder='Enter founder name'
                  onChange={(curretnVakue) => {
                    onChange(curretnVakue);
                    setIsSelectedAll(false);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: !!isLoading && (
                      <CircularProgress color='primary' size={20} />
                    ),
                  }}
                />
              )}
            />
          </FormGroup>
          <div className={classes.chips}>
            {initialFounders.map((founder) => (
              <Chip
                key={founder.id}
                label={founder.name}
                onDelete={() => handleFounderRemove(founder)}
                className={classes.chipItem}
              />
            ))}
            {isSelectedAll && (
              <Chip
                data-testid='mentor-chip-all'
                key='selected-all'
                label='All'
                onDelete={() => setIsSelectedAll(false)}
                className={classes.chipItem}
              />
            )}
          </div>
          {!isSelectedAll && displayedList.length > 0 && (
            <AssignmentRecommendedTable
              labels={['Founders', '']}
              rows={displayedList.map((recomItem) => ({
                name: recomItem.name,
                onAdd: () => handleFounderAdd(recomItem),
              }))}
              mobileFullHeight
            />
          )}
          <FormButtons className={classes.formButtons}>
            <Button onClick={onCancel} variant='outlined'>
              Cancel
            </Button>

            <Button data-testid='button-add-all' onClick={handleSelectedAll}>
              Add all
            </Button>
            <Button disabled={loading} type='submit' data-testid='button-apply'>
              {loading ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Apply'
              )}
            </Button>
          </FormButtons>
        </form>
      )}
    />
  );
}

export default InviteGatheringFounders;
